import type { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { commonLanguageFiles } from "client/config";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { translateFunc } from "client/utils/translateFunc";
import { ColorPickerContent } from "components/modules/Content/ColorPickerContent/ColorPickerContent";
import { ColorPickerTutorial } from "components/modules/Tutorial/ColorPickerTutorial/ColorPickerTutorial";
import { ColorHarmoniesContent } from "components/modules/Editors/ColorPicker/ColorHarmoniesContent/ColorHarmoniesContent";
import { Footer } from "components/modules/Layout/Footer/Footer";
import dynamic from "next/dynamic";
import { TrustPilotSection } from "components/modules/TrustPilot/TrustPilotSection";
import React from "react";

const ColorPicker = dynamic<{}>(() =>
    import("components/modules/Editors/ColorPicker/ColorPicker").then((mod) => mod.ColorPicker),
);

const ColorPickerPage = () => {
    const title = translateFunc("page-title");
    const description = translateFunc("meta-description");

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
                <meta property="twitter:description" content={description} />
            </Head>
            <div className="max-w-1440 page-shadow dark:bg-darkSurface-100">
                <ColorPicker />

                <div
                    className="mx-auto w-full"
                    style={{
                        maxWidth: "840px",
                    }}
                >
                    <div className="pt-16 pb-36">
                        <ColorPickerContent />
                        <ColorPickerTutorial />
                        <ColorHarmoniesContent />
                    </div>
                </div>
                <TrustPilotSection />
                <Footer url={"/color-picker"} />
            </div>
        </>
    );
};

const files = ["color-picker", "uploader", ...commonLanguageFiles];

const LanguageWrapper = () => (
    <LanguageProvider files={files}>
        <ColorPickerPage />
    </LanguageProvider>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
